import { useState, useEffect, useCallback, useMemo } from "react";
import { ExchangeClient, InfoClient, toSatoshi, toSatoshiUint64 } from "../sdk";
import { toNumberFromSatoshi } from "../sdk/utils/math";
import {
  LEVERAGE_DEFAULT,
  POSITION_MODE_ONE_WAY,
  MARGIN_MODE_CROSS,
  ORDER_TYPE_MARKET,
} from "../components/smartlayout/constants";
import { TradeOperationConfig, TradeResult } from "../sdk/types/data";
import { useMarkets } from "./useMarkets";

// Re-export for backward compatibility
export type { TradeOperationConfig, TradeResult };

export function useTradeOperation(config: TradeOperationConfig) {
  const [exchangeClient, setExchangeClient] = useState<ExchangeClient | null>(
    null,
  );
  const [infoClient, setInfoClient] = useState<InfoClient | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<TradeResult | null>(null);

  // Trading state
  const [orderType, setOrderType] = useState<"market" | "limit">(
    ORDER_TYPE_MARKET,
  );
  const [fundPercent, setFundPercent] = useState<number>(0);
  const [sizeInput, setSizeInput] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [leverage, setLeverage] = useState<number>(LEVERAGE_DEFAULT);
  const [positionMode, setPositionMode] = useState<"ONE_WAY" | "HEDGE">(
    POSITION_MODE_ONE_WAY,
  );
  const [marginMode, setMarginMode] = useState<"CROSS" | "ISOLATED">(
    MARGIN_MODE_CROSS,
  );

  // Balance and market data
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [marketId, setMarketId] = useState<string>(
    config.marketId ? String(config.marketId) : "1",
  );

  // Sync marketId when config.marketId changes
  useEffect(() => {
    if (config.marketId) {
      setMarketId(String(config.marketId));
    } else {
      setMarketId("1");
    }
  }, [config.marketId]);

  // Get market data to access maxSizeLimit and minSizeLimit
  const { markets } = useMarkets({
    baseURL: config.baseURL,
    autoFetchMarkets: true,
  });

  // Get maxSizeLimit and minSizeLimit from market data by market id
  const { maxSizeLimit, minSizeLimit } = useMemo(() => {
    if (!marketId) return { maxSizeLimit: undefined, minSizeLimit: undefined };

    // Try to find market by market_id (string) or id (string)
    const market = markets.find((m) => m.market_id === marketId);

    if (!market) return { maxSizeLimit: undefined, minSizeLimit: undefined };

    return {
      maxSizeLimit: market.maxSizeLimit || 40,
      minSizeLimit: market.minSizeLimit || 0.001,
    };
  }, [marketId, markets]);

  // Update available balance when selectedBucketBalance changes
  // This allows bucket selection to override the API-loaded balance
  useEffect(() => {
    if (config.selectedBucketBalance !== undefined) {
      // Use bucket balance if provided (even if 0, as it's an explicit selection)
      setAvailableBalance(config.selectedBucketBalance);
    }
    // If undefined, the balance will be reloaded from API by the effect below
    // when clients are ready
  }, [config.selectedBucketBalance]);

  // Initialize clients
  useEffect(() => {
    // Validate mnemonic before initializing ExchangeClient
    // Skip initialization if mnemonic is empty or invalid
    if (!config.mnemonic || config.mnemonic.trim() === "") {
      console.warn(
        "[useTradeOperation] Skipping ExchangeClient initialization: mnemonic is empty",
      );
      setExchangeClient(null);
      setInfoClient(new InfoClient({ baseURL: config.baseURL }));
      setIsReady(false);
      return;
    }

    const exchange = new ExchangeClient({
      baseURL: config.baseURL,
      mnemonic: config.mnemonic,
    });
    const info = new InfoClient({
      baseURL: config.baseURL,
    });

    setExchangeClient(exchange);
    setInfoClient(info);

    // Initialize exchange client
    exchange
      .initialize()
      .then(() => {
        setIsReady(true);
      })
      .catch((error) => {
        console.error("Failed to initialize exchange client:", error);
        setResult({
          success: false,
          error: `Initialization failed: ${error.message}`,
        });
      });
  }, [config.baseURL, config.mnemonic]);

  // Load balance
  const loadBalance = useCallback(async () => {
    if (!infoClient || !exchangeClient || !isReady) return;
    // Don't override balance if a bucket balance is explicitly selected
    if (config.selectedBucketBalance !== undefined) {
      return;
    }
    try {
      const walletAddress = exchangeClient.getWalletAddress();
      const balance = await infoClient.getBalanceDecimal(walletAddress, "MORM");
      if (balance && balance.available_balance !== undefined) {
        // Use utility function from math.ts for consistent conversion
        setAvailableBalance(toNumberFromSatoshi(balance.available_balance));
      }
    } catch (error) {
      console.error("Failed to load balance:", error);
    }
  }, [infoClient, exchangeClient, isReady, config.selectedBucketBalance]);

  // Load balance when clients are ready and no bucket balance is selected
  useEffect(() => {
    if (
      isReady &&
      infoClient &&
      exchangeClient &&
      config.selectedBucketBalance === undefined
    ) {
      loadBalance();
    }
  }, [
    isReady,
    infoClient,
    exchangeClient,
    config.selectedBucketBalance,
    loadBalance,
  ]);

  // Load current market price
  const loadMarketPrice = useCallback(async () => {
    if (!infoClient) return;
    try {
      const ticker = await infoClient.getMarketStats(marketId);
      if (ticker && ticker.last_price) {
        // Use utility function from math.ts for consistent conversion
        const priceValue = toNumberFromSatoshi(ticker.last_price);
        setCurrentPrice(priceValue);
      }
    } catch (error) {
      console.error("Failed to load market price:", error);
    }
  }, [infoClient, marketId]);

  // Load market price when ready
  useEffect(() => {
    if (isReady && infoClient) {
      loadMarketPrice();
    }
  }, [isReady, infoClient, loadMarketPrice]);

  // Calculate order price (market or limit)
  const getOrderPrice = useCallback((): number => {
    if (orderType === "market") {
      // Use trade price from useTradePrice hook if available, otherwise fallback to currentPrice
      return config.tradePrice && config.tradePrice > 0
        ? config.tradePrice
        : currentPrice;
    } else {
      return price || 0;
    }
  }, [orderType, price, currentPrice, config.tradePrice]);

  // Calculate available trade amount (balance * leverage)
  const getAvailableTradeAmount = useCallback((): number => {
    return availableBalance * leverage;
  }, [availableBalance, leverage]);

  // Calculate order size in BTC quantity
  const calculateSize = useCallback((): number => {
    let size: number;
    // If sizeInput is set (> 0), use it directly (it's already asset quantity)
    if (sizeInput > 0) {
      size = sizeInput;
    } else {
      // Otherwise calculate from fund percentage
      const availableTradeAmount = getAvailableTradeAmount();
      const sizeUSD = (availableTradeAmount * fundPercent) / 100;
      const orderPrice = getOrderPrice();
      if (orderPrice <= 0) return 0;
      size = sizeUSD / orderPrice;
    }
    // Apply size limits if configured
    // Enforce minimum size limit
    if (minSizeLimit !== undefined && minSizeLimit > 0 && size < minSizeLimit) {
      return 0; // Return 0 if below minimum (will be caught by validation)
    }
    // Cap at maxSizeLimit
    if (maxSizeLimit !== undefined && maxSizeLimit > 0 && size > maxSizeLimit) {
      return maxSizeLimit;
    }
    return size;
  }, [
    sizeInput,
    fundPercent,
    getAvailableTradeAmount,
    getOrderPrice,
    maxSizeLimit,
    minSizeLimit,
  ]);

  // Calculate order size in USD value (notional)
  const calculateSizeUSD = useCallback((): number => {
    const sizeBTC = calculateSize();
    const orderPrice = getOrderPrice();
    return sizeBTC * orderPrice;
  }, [calculateSize, getOrderPrice]);

  // Calculate order value (notional value in USD) - returns in satoshi format
  const calculateValue = useCallback((): number => {
    const sizeBTC = calculateSize();
    const orderPrice = getOrderPrice();
    const valueUSD = sizeBTC * orderPrice;
    // Convert decimal USD value to satoshi format
    return toSatoshiUint64(valueUSD);
  }, [calculateSize, getOrderPrice]);

  // Calculate margin required - returns in satoshi format
  const calculateMargin = useCallback((): number => {
    const value = calculateValue();
    if (leverage <= 0) return 0;
    // Value is already in satoshi, so divide by leverage to get margin in satoshi
    return Math.floor(value / leverage);
  }, [calculateValue, leverage]);

  // Place buy order
  const placeBuyOrder = useCallback(async (): Promise<void> => {
    if (!exchangeClient || !isReady) {
      setResult({
        success: false,
        error: "Exchange client not ready",
      });
      return;
    }

    const size = calculateSize();
    if (size <= 0) {
      setResult({
        success: false,
        error: "Invalid order size",
      });
      return;
    }

    const orderPrice = getOrderPrice();

    // Validate price for both limit and market orders
    // ExchangeClient requires price > 0 even for market orders
    if (orderPrice <= 0) {
      setResult({
        success: false,
        error:
          orderType === "limit"
            ? "Invalid price for limit order"
            : "Market price not available. Please wait for price data to load.",
      });
      return;
    }

    if (orderType === "limit" && !price) {
      setResult({
        success: false,
        error: "Price is required for limit orders",
      });
      return;
    }

    // Validate bucketId is provided
    if (!config.selectedBucketId || config.selectedBucketId.trim() === "") {
      setResult({
        success: false,
        error: "Bucket ID is required for order placement",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const leverageStr = toSatoshi(leverage.toString());
      const response = await exchangeClient.placeOrder({
        marketId,
        bucketId: config.selectedBucketId,
        side: "BUY",
        orderType,
        price: orderPrice,
        quantity: size,
        leverage: leverageStr,
      });

      setResult({
        success: true,
        orderId: response.order_id,
        message: "Buy order placed successfully",
      });

      // Reload balance after order
      await loadBalance();
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    exchangeClient,
    isReady,
    calculateSize,
    getOrderPrice,
    orderType,
    price,
    leverage,
    marketId,
    config.selectedBucketId,
    loadBalance,
  ]);

  // Place sell order
  const placeSellOrder = useCallback(async (): Promise<void> => {
    if (!exchangeClient || !isReady) {
      setResult({
        success: false,
        error: "Exchange client not ready",
      });
      return;
    }

    const size = calculateSize();
    if (size <= 0) {
      setResult({
        success: false,
        error: "Invalid order size",
      });
      return;
    }

    const orderPrice = getOrderPrice();

    // Validate price for both limit and market orders
    // ExchangeClient requires price > 0 even for market orders
    if (orderPrice <= 0) {
      setResult({
        success: false,
        error:
          orderType === "limit"
            ? "Invalid price for limit order"
            : "Market price not available. Please wait for price data to load.",
      });
      return;
    }

    if (orderType === "limit" && !price) {
      setResult({
        success: false,
        error: "Price is required for limit orders",
      });
      return;
    }

    // Validate bucketId is provided
    if (!config.selectedBucketId || config.selectedBucketId.trim() === "") {
      setResult({
        success: false,
        error: "Bucket ID is required for order placement",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const leverageStr = toSatoshi(leverage.toString());
      const response = await exchangeClient.placeOrder({
        marketId,
        bucketId: config.selectedBucketId,
        side: "SELL",
        orderType,
        price: orderPrice,
        quantity: size,
        leverage: leverageStr,
      });

      setResult({
        success: true,
        orderId: response.order_id,
        message: "Sell order placed successfully",
      });

      // Reload balance after order
      await loadBalance();
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    exchangeClient,
    isReady,
    calculateSize,
    getOrderPrice,
    orderType,
    price,
    leverage,
    marketId,
    config.selectedBucketId,
    loadBalance,
  ]);

  // Change leverage for a position
  const changeLeverage = useCallback(
    async (
      newLeverage: number,
      options: {
        bucketId: string; // Required: bucket ID for the position
        leverageType?: string;
        reason?: string;
      },
    ): Promise<void> => {
      if (!exchangeClient || !isReady) {
        setResult({
          success: false,
          error: "Exchange client not ready",
        });
        return;
      }

      if (!marketId || marketId.trim() === "") {
        setResult({
          success: false,
          error: "Market ID is required",
        });
        return;
      }

      if (newLeverage <= 0) {
        setResult({
          success: false,
          error: "Leverage must be greater than 0",
        });
        return;
      }

      if (!options.bucketId || options.bucketId.trim() === "") {
        setResult({
          success: false,
          error: "Bucket ID is required",
        });
        return;
      }

      setLoading(true);
      setResult(null);

      try {
        const response = await exchangeClient.changeLeverage({
          marketId,
          newLeverage,
          bucketId: options.bucketId,
          leverageType: options.leverageType,
          reason: options.reason,
        });

        // Update local leverage state on success
        setLeverage(newLeverage);

        setResult({
          success: true,
          message: "Leverage changed successfully",
          orderId: response.txhash,
        });

        // Reload balance after leverage change
        await loadBalance();
      } catch (error) {
        setResult({
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setLoading(false);
      }
    },
    [exchangeClient, isReady, marketId, loadBalance],
  );

  // Update size from fund percentage slider - this updates sizeInput which syncs all three places
  const handleFundPercentChange = useCallback(
    (percent: number) => {
      const availableTradeAmount = getAvailableTradeAmount();
      const sizeUSD = (availableTradeAmount * percent) / 100;
      // Calculate quantity using price for size calculation
      const orderPrice = getOrderPrice();
      const size = orderPrice > 0 ? sizeUSD / orderPrice : 0;

      // Apply size limits
      let roundedSize = size;

      // Enforce minimum size limit
      if (
        minSizeLimit !== undefined &&
        minSizeLimit > 0 &&
        roundedSize < minSizeLimit
      ) {
        roundedSize = 0; // Set to 0 if below minimum
      }

      // Cap at maxSizeLimit
      if (
        maxSizeLimit !== undefined &&
        maxSizeLimit > 0 &&
        roundedSize > maxSizeLimit
      ) {
        roundedSize = maxSizeLimit;
        // Recalculate percentage based on the capped size to keep slider in sync
        if (roundedSize > 0 && availableTradeAmount > 0 && orderPrice > 0) {
          const cappedSizeUSD = roundedSize * orderPrice;
          const actualPercent = (cappedSizeUSD / availableTradeAmount) * 100;
          setFundPercent(Math.min(100, Math.max(0, actualPercent)));
        } else {
          setFundPercent(percent);
        }
      } else {
        setFundPercent(percent);
      }
      setSizeInput(roundedSize);
    },
    [getAvailableTradeAmount, getOrderPrice, maxSizeLimit, minSizeLimit],
  );

  // Update fund percentage from size input
  const handleSizeInputChange = useCallback(
    (value: number) => {
      let numValue = isNaN(value) || value < 0 ? 0 : value;
      // Apply size limit if configured - cap at maxSizeLimit
      if (
        maxSizeLimit !== undefined &&
        maxSizeLimit > 0 &&
        numValue > maxSizeLimit
      ) {
        numValue = maxSizeLimit;
      }
      // Use the actual React setter directly
      setSizeInput(numValue);
      const availableTradeAmount = getAvailableTradeAmount();
      const orderPrice = getOrderPrice();
      if (numValue > 0 && availableTradeAmount > 0 && orderPrice > 0) {
        // Convert asset quantity to USD, then calculate fund percentage
        const sizeUSD = numValue * orderPrice;
        const percent = (sizeUSD / availableTradeAmount) * 100;
        setFundPercent(Math.min(100, Math.max(0, percent)));
      } else {
        setFundPercent(0);
      }
    },
    [getAvailableTradeAmount, getOrderPrice, maxSizeLimit],
  );

  return {
    // State
    orderType,
    setOrderType,
    fundPercent,
    sizeInput,
    price,
    leverage,
    positionMode,
    marginMode,
    availableBalance,
    currentPrice,
    marketId,
    isReady,
    loading,
    result,

    // Actions
    setFundPercent: handleFundPercentChange,
    setSizeInput: handleSizeInputChange,
    setPrice,
    setLeverage,
    setPositionMode,
    setMarginMode,
    setMarketId,
    placeBuyOrder,
    placeSellOrder,
    changeLeverage,
    loadBalance,
    loadMarketPrice,

    // Calculations
    calculateSize,
    calculateSizeUSD,
    calculateValue,
    calculateMargin,
    getOrderPrice,
    getAvailableTradeAmount,
  };
}
